"use client";
import { solid } from "@areahub/icons";
import { Box, chakra, HStack, Icon, Stack, Text } from "@chakra-ui/react";
import { Link } from "@ui/components/link/link";
import { getLanguage, useLang } from "@ui/utils";
import { capitalCase } from "change-case";
import {
  FaFacebook,
  FaInstagram,
  FaLink,
  FaLinkedin,
  FaReddit,
  FaTiktok,
  FaTwitter,
  FaYoutube,
} from "react-icons/fa";
import { PostAuthorImage } from "../media";
import { GenericAuthor, SocialPlatform } from "../types";
const MapMarker = solid["map-marker"];
const Pen = solid["pen"];

export interface PostHeaderAuthorProps<AuthorType extends GenericAuthor> {
  author: AuthorType;
}

const icons = {
  facebook: FaFacebook,
  instagram: FaInstagram,
  linkedin: FaLinkedin,
  reddit: FaReddit,
  tiktok: FaTiktok,
  twitter: FaTwitter,
  website: FaLink,
  youtube: FaYoutube,
};

export const PostHeaderAuthor = <AuthorType extends GenericAuthor>({
  author,
}: PostHeaderAuthorProps<AuthorType>) => {
  const text = getLanguage(useLang());

  if (!author || !author.socialLinks) {
    console.warn("PostHeaderAuthor: Author data is missing or incomplete.");
    return null;
  }

  const socialMap: { [key in SocialPlatform]?: string } = {};
  author.socialLinks?.forEach(
    ({ platform, url }) => (socialMap[platform] = url),
  );

  const profileImg = author?.photoPlaceholder;
  const authorName = author.person?.name;
  const numberOfPosts = author.posts.length;

  return (
    <header>
      <Box
        maxW="7xl"
        pt="24"
        mx="auto"
        px={{
          sm: "4",
          md: "6",
        }}
      >
        <Stack
          direction={{ base: "column", md: "row" }}
          spacing={{ base: "4", md: "10" }}
        >
          <PostAuthorImage
            sx={{
              w: "40",
              minW: "40",
              h: "40",
            }}
            image={profileImg}
            style={{ objectFit: "cover" }}
            url={`/individuals/knowledge-center/author/${author.slug}`}
            name={authorName || ""}
          />

          <Box>
            <Text as="h1" fontWeight="extrabold" fontSize="5xl" mb="2">
              {authorName}
            </Text>
            <Text apply="styles.mdx.p" color={"gray.500"} fontWeight="medium">
              {author.biography}
            </Text>
            <HStack
              alignItems="center"
              spacing={{ base: "4", lg: "6" }}
              fontSize="sm"
              my="2"
              color="gray.500"
            >
              <HStack>
                <Icon as={Pen} />
                <Text>
                  {(numberOfPosts &&
                    ` ${numberOfPosts} ${
                      1 < numberOfPosts ? "Posts" : "Post"
                    }`) ||
                    `${text(`NO_POSTS`)}`}
                </Text>
              </HStack>
              {author.location && (
                <HStack>
                  <Icon as={MapMarker} />
                  <Text>{author.location}</Text>
                </HStack>
              )}
              {/** List social media platforms */}
              {Object.entries(socialMap).map(([key, value], i) => {
                return (
                  <HStack key={`${key}-${i}`}>
                    <Icon as={icons[key as keyof typeof icons]} />
                    <Link
                      href={value as string}
                      isExternal
                      apply="styles.mdx.a"
                    >
                      {capitalCase(key)}
                    </Link>
                  </HStack>
                );
              })}
            </HStack>
          </Box>
        </Stack>
        <chakra.hr apply="styles.mdx.hr" />
        <chakra.h2 apply="styles.mdx.h2">Articles by {authorName}</chakra.h2>
      </Box>
    </header>
  );
};

export default PostHeaderAuthor;
